// Active-session + start-capture render branches for
// BuildingCaptureScreen, extracted from building-capture-screen.tsx to keep
// that file under the max-lines policy. Owns the per-session state (room
// dialog, status toggle, soft-warn dialog) — the screen owns data-loading
// and session-start above it.
//
// Both roles capture buildings: the original §1.1a admin-preview branch was
// removed 2026-06-11 at the product owner's request (training/support
// workflows). Deep-link safety (§1.1) is role-independent: navigation never
// auto-creates a session — the StartCaptureCard tap fires the POST.

import { useCallback, useState, type ReactElement } from 'react'
import { Plus, X } from 'lucide-react'
import { useNavigate } from 'react-router'

import {
  type BuildingStatus,
  type SessionDetail,
  type SessionSummary,
  type SpaceWithItems,
} from '@citycare/shared'

import { useFloorOptions } from '../../hooks/powersync/use-floor-options'
import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'

import { useRegisteredCaptureClient } from '../client/use-registered-capture-client'
import { CancelSessionDialog } from '../components/cancel-session-dialog'
import { CaptureHero } from '../components/capture-hero'
import {
  DeleteRoomDialog,
  countCapturedAssets,
} from '../components/delete-room-dialog'
import { EditAliasDialog } from '../components/edit-alias-dialog'
import { FloorSection } from '../components/floor-section'
import {
  NewRoomDialog,
  type NewRoomDialogFloor,
  type NewRoomDialogRoomType,
} from '../components/new-room-dialog'
import { StartCaptureCard } from '../components/start-capture-card'

import { CaptureStatusBar } from './capture-status-bar'

import {
  aggregateAssetCounts,
  findContinueTarget,
  orderFloors,
} from './building-capture-helpers'
import { useCancelSession } from './use-cancel-session'
import { useDeleteRoom } from './use-delete-room'
import { useEditRoomAlias } from './use-edit-room-alias'

export interface BuildingFloor {
  id: string
  name: string
  displayOrder: number
}

export interface BuildingMetadata {
  id: string
  siteId: string
  name: string
  floc: string | null
  status: BuildingStatus
  floors: readonly BuildingFloor[]
  parent: { site: { id: string; name: string }; base: { id: string; name: string } }
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export interface BuildingCaptureContentProps {
  building: BuildingMetadata
  session: SessionSummary | null
  sessionDetail: SessionDetail | null
  roomTypes: readonly NewRoomDialogRoomType[]
  onStart?: () => void
  isStarting?: boolean
}

export function BuildingCaptureContent({
  building,
  session,
  sessionDetail,
  roomTypes,
  onStart,
  isStarting = false,
}: BuildingCaptureContentProps): ReactElement {
  const navigate = useNavigate()
  // #726: creates + registers for logout-flush + destroys on unmount.
  const captureClient = useRegisteredCaptureClient()
  const floorOptionsResult = useFloorOptions()
  const [newRoomOpen, setNewRoomOpen] = useState(false)
  const [addedSpaces, setAddedSpaces] = useState<SpaceWithItems[]>([])
  const [buildingStatus, setBuildingStatus] = useState<BuildingStatus | null>(null)
  const [currentSyncVersion, setCurrentSyncVersion] = useState<number>(session?.syncVersion ?? 1)
  const cancelDialog = useCancelSession({
    captureClient,
    sessionId: session?.id ?? null,
    syncVersion: currentSyncVersion,
  })
  const onConfirmCancel = useCallback(async () => {
    const result = await cancelDialog.confirm()
    if (result === null) return
    void navigate(`/capture/sites/${building.siteId}`)
  }, [cancelDialog, navigate, building.siteId])
  // Tracks the in-flight Mark complete / Reopen dispatch so the button
  // can disable itself and surface any rejection.
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  // Soft-warn before marking a building complete when no assets have
  // been captured yet — a "did you mean to do that?" guard, not a hard
  // block (legitimate completions of empty buildings, e.g. condemned
  // structures, remain possible).
  const [confirmEmptyCompleteOpen, setConfirmEmptyCompleteOpen] = useState(false)

  // Field delete of rooms (ADR-015) — state machine lives in the hook so
  // this file stays under the lint:max-lines cap (mirrors useCancelSession).
  const deleteRoom = useDeleteRoom({
    captureClient,
    sessionId: session?.id ?? '',
  })

  // Field rename of rooms (FFR2) — alias-edit state machine, same split.
  const editAlias = useEditRoomAlias({
    captureClient,
    sessionId: session?.id ?? '',
  })

  if (session !== null) {
    const effectiveStatus: BuildingStatus =
      buildingStatus ?? session.building?.status ?? 'not_started'

    // Exclude rooms the user has optimistically deleted (ADR-015) so the
    // list updates immediately on confirm; a dispatch error restores the
    // id and the room reappears. Then apply any optimistic alias overrides
    // (FFR2) so a rename shows immediately and survives a server DTO
    // snapshot that predates the edit — a space absent from the override
    // map keeps its own alias.
    const allSpaces = [
      ...(sessionDetail?.spaces ?? []),
      ...addedSpaces,
    ]
      .filter((s) => !deleteRoom.deletedIds.has(s.id))
      .map((s) =>
        editAlias.aliasOverrides.has(s.id)
          ? { ...s, alias: editAlias.aliasOverrides.get(s.id) ?? null }
          : s,
      )
    const orderedFloors = orderFloors(building.floors, allSpaces)

    // Picker source is contract-scoped floor_options (admin-curated),
    // not the per-building `floors` rows. building.floors above still
    // groups already-captured rooms so renames/deletes of the global
    // floor config don't disturb existing data.
    const dialogFloors: NewRoomDialogFloor[] = floorOptionsResult.floorOptions.map(
      (f) => ({ id: f.id, name: f.name }),
    )

    const goToRoom = (spaceId: string): void => {
      void navigate(`/capture/spaces/${spaceId}`, { state: { sessionId: session.id } })
    }

    const { totalAssets, doneAssets } = aggregateAssetCounts(allSpaces)
    const continueTarget = findContinueTarget(orderedFloors)

    // Status toggle: complete <-> in_progress. Reopening drops the
    // user straight into the next incomplete room; marking complete
    // sends the surveyor back to the site's building list — there's
    // nothing left to do on this building screen.
    const isComplete = effectiveStatus === 'complete'

    // Empty-capture guard: a building marked complete with zero
    // captured assets is almost always a misclick. Treat it as a
    // soft warn rather than a hard block — see comment on
    // confirmEmptyCompleteOpen for the rationale.
    const hasNoCaptures = doneAssets === 0

    const performStatusDispatch = async (): Promise<void> => {
      if (togglingStatus) return
      const nextStatus: BuildingStatus = isComplete ? 'in_progress' : 'complete'
      setTogglingStatus(true)
      setStatusError(null)
      try {
        const result = await captureClient.dispatch(
          isComplete
            ? {
                kind: 'reopenSession',
                sessionId: session.id,
                syncVersion: currentSyncVersion,
              }
            : {
                kind: 'completeSession',
                sessionId: session.id,
                syncVersion: currentSyncVersion,
              },
        )
        if (
          result.kind === 'completeSession' ||
          result.kind === 'reopenSession'
        ) {
          const sessionResult = result.session as SessionSummary
          setCurrentSyncVersion(sessionResult.syncVersion)
          // audit-2-#5: trust the server-reported status, not the optimistic nextStatus (freeze-guarded writes no-op server-side, audit-1-#8).
          setBuildingStatus(sessionResult.building?.status ?? nextStatus)
        }
        if (!isComplete) {
          // Just marked complete — pop back to the site's building list.
          void navigate(`/capture/sites/${building.siteId}`)
          return
        }
        if (
          continueTarget !== undefined &&
          allSpaces.some((s) => s.id === continueTarget.id)
        ) {
          goToRoom(continueTarget.id)
        }
      } catch (err: unknown) {
        setStatusError(describeError(err))
      } finally {
        setTogglingStatus(false)
      }
    }

    const handleStatusToggle = async (): Promise<void> => {
      if (togglingStatus) return
      // Reopen path doesn't need the empty-capture warn — only the
      // forward "mark complete" direction.
      if (!isComplete && hasNoCaptures) {
        setConfirmEmptyCompleteOpen(true)
        return
      }
      await performStatusDispatch()
    }

    // ADR-015 — confirm + dispatch the room hard-delete. The hook does
    // the optimistic removal + reconcile; here we just apply the
    // recomputed building status it returns on success.
    const handleConfirmDelete = async (): Promise<void> => {
      const nextStatus = await deleteRoom.confirm()
      if (nextStatus !== null) setBuildingStatus(nextStatus)
    }

    return (
      <section
        className="flex flex-col gap-4 pb-safe"
        aria-label={`Capture ${building.name}`}
      >
        {/* CaptureHero's <h2> carries the section heading. The Mark
            complete / Reopen action lives in the sticky bottom bar
            below — surveyors continue capturing by tapping a room
            directly, so a hero-level CTA was redundant noise. The
            destructive Cancel session affordance sits in the hero's
            top-right corner via `headerAction`; it stays hidden once
            the building is complete — same gate as before. */}
        <CaptureHero
          eyebrow="Building"
          name={building.name}
          subtitle={building.floc}
          countLine={
            totalAssets > 0
              ? `${doneAssets}/${totalAssets} assets captured`
              : null
          }
          headerAction={
            !isComplete
              ? {
                  label: 'Cancel building capture session',
                  icon: <X aria-hidden="true" className="h-5 w-5" />,
                  onClick: cancelDialog.open,
                  disabled: togglingStatus || cancelDialog.isSubmitting,
                  testId: 'cancel-building-session-btn',
                }
              : undefined
          }
        />

        {statusError !== null ? (
          <Alert
            variant="error"
            title={
              isComplete
                ? 'Could not reopen building'
                : 'Could not mark building complete'
            }
          >
            {statusError}
          </Alert>
        ) : null}

        {orderedFloors.length > 0 ? (
          <div className="flex flex-col gap-4">
            {orderedFloors.map((floor) => (
              <FloorSection
                key={floor.id}
                floorName={floor.name}
                spaces={floor.spaces}
                onRoomClick={(space) => goToRoom(space.id)}
                // Rename + delete affordances are owner-only (this screen
                // always renders the current user's own active session)
                // and gated to an in-progress building, consistent with
                // how Add room is gated (FFR2 rename mirrors ADR-015
                // delete).
                onRoomEditAlias={
                  !isComplete
                    ? (space) =>
                        editAlias.open({
                          id: space.id,
                          displayName: space.displayName,
                          alias: space.alias,
                        })
                    : undefined
                }
                onRoomDelete={
                  !isComplete ? (space) => deleteRoom.open(space) : undefined
                }
              />
            ))}
          </div>
        ) : null}

        {/* Add room is gated behind the in-progress state — a complete
            building takes no structural changes without a reopen. */}
        {!isComplete ? (
          <button
            type="button"
            onClick={() => setNewRoomOpen(true)}
            className="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-2xl border-[1.5px] border-dashed border-[color:var(--color-cc-forest)]/30 bg-transparent px-4 py-3.5 text-sm font-semibold text-[color:var(--color-cc-forest)] transition hover:border-[color:var(--color-cc-forest)] hover:bg-white/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
            data-testid="new-room-button"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Add room
          </button>
        ) : null}

        {/* Sticky bar — Mark complete / Reopen toggle; frozen buildings get
            the read-only badge beside the disabled CTA. Lives in
            ./capture-status-bar.tsx (the max-lines split idiom). */}
        <CaptureStatusBar
          effectiveStatus={effectiveStatus}
          isComplete={isComplete}
          togglingStatus={togglingStatus}
          onToggle={() => void handleStatusToggle()}
        />

        <CancelSessionDialog
          open={cancelDialog.isOpen}
          scope="building"
          scopeName={building.name}
          submitting={cancelDialog.isSubmitting}
          error={cancelDialog.error}
          onClose={cancelDialog.close}
          onConfirm={() => void onConfirmCancel()}
        />

        <NewRoomDialog
          open={newRoomOpen}
          onClose={() => setNewRoomOpen(false)}
          sessionId={session.id}
          floors={dialogFloors}
          floorsLoading={floorOptionsResult.isLoading}
          roomTypes={roomTypes}
          dispatch={(mutation) => captureClient.dispatch(mutation)}
          onCreated={(space) => {
            setAddedSpaces((prev) => [...prev, space])
            // Carry the just-created space through nav state so the
            // room screen can render it immediately, even before the
            // PowerSync local mirror has caught up. Without this the
            // room screen reads `session.spaces.find(...)` and gets
            // undefined → "Room not found" alert until reload.
            void navigate(`/capture/spaces/${space.id}`, {
              state: { sessionId: session.id, space },
            })
          }}
        />

        <DeleteRoomDialog
          open={deleteRoom.target !== null}
          roomName={deleteRoom.target?.displayName ?? ''}
          capturedCount={
            deleteRoom.target !== null
              ? countCapturedAssets(deleteRoom.target.items)
              : 0
          }
          submitting={deleteRoom.isDeleting}
          error={deleteRoom.error}
          onCancel={deleteRoom.close}
          onConfirm={() => void handleConfirmDelete()}
        />

        {/* Rename dialog (FFR2). Keyed on the target room id so the input
            re-seeds per room (avoids a set-state-in-effect prefill sync). */}
        {editAlias.target !== null ? (
          <EditAliasDialog
            key={editAlias.target.id}
            open
            roomName={editAlias.target.displayName}
            currentAlias={editAlias.target.alias}
            submitting={editAlias.isSaving}
            error={editAlias.error}
            onCancel={editAlias.close}
            onConfirm={(alias) => void editAlias.confirm(alias)}
          />
        ) : null}

        {confirmEmptyCompleteOpen ? (
          <Dialog
            open
            onClose={() => {
              if (!togglingStatus) setConfirmEmptyCompleteOpen(false)
            }}
            title="Mark this building complete?"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                You haven't captured any assets in{' '}
                <strong>{building.name}</strong> yet. Marking it complete now
                will record zero captures against this building.
              </p>
              <p className="text-sm text-gray-700">
                You can still mark it complete if that's correct (for example,
                a vacant or condemned structure), or cancel and keep adding
                rooms or assets.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConfirmEmptyCompleteOpen(false)}
                  disabled={togglingStatus}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setConfirmEmptyCompleteOpen(false)
                    void performStatusDispatch()
                  }}
                  disabled={togglingStatus}
                >
                  {togglingStatus ? 'Marking complete…' : 'Mark complete anyway'}
                </Button>
              </div>
            </div>
          </Dialog>
        ) : null}
      </section>
    )
  }

  // No session (either role): explicit start-capture gesture. When the
  // building is already 'complete' (or 'in_progress' with no active
  // session owned by this user — the orphan case), StartCaptureCard
  // flips to a "resume" framing. Underlying POST is unchanged.
  return (
    <StartCaptureCard
      buildingName={building.name}
      buildingFloc={building.floc}
      buildingStatus={building.status}
      onStart={onStart}
      isStarting={isStarting}
    />
  )
}
